import { type ClientContext, ContextClient } from '~/services/contextClient';
import SnippetsClientFactory from './snippetsClient.factory';
import type { SnippetsClientInterface } from './snippetsClient.types';
import type { Snippet } from '~/types/snippet.types';
import type { CursorPaginationResponse, QueryParams } from '~/services/api';
import type { AxiosPromise } from 'axios';
import type { ExtendedAxiosRequestConfig } from '~/services/BaseClient';

class SnippetsClient extends ContextClient<SnippetsClientInterface> {
    constructor() {
        super(SnippetsClientFactory);
    }

    getSnippets(
        context: ClientContext,
        params?: QueryParams,
        config?: ExtendedAxiosRequestConfig
    ): AxiosPromise<CursorPaginationResponse<Snippet>> {
        const client = this.getClient(context);
        return client.getSnippets(params, config);
    }

    getSnippetsCount(context: ClientContext, params?: QueryParams): AxiosPromise<{ count: number }> {
        const client = this.getClient(context);
        return client.getSnippetsCount(params);
    }

    getSnippet(context: ClientContext, snippetId: number): AxiosPromise<Snippet> {
        const client = this.getClient(context);
        return client.getSnippet(snippetId);
    }

    createSnippet(context: ClientContext, data: Snippet): AxiosPromise<Snippet> {
        const client = this.getClient(context);
        return client.createSnippet(data);
    }

    updateSnippet(context: ClientContext, snippetId: number, data: Snippet): AxiosPromise<Snippet> {
        const client = this.getClient(context);
        return client.updateSnippet(snippetId, data);
    }

    deleteSnippet(context: ClientContext, snippetId: number): AxiosPromise<void> {
        const client = this.getClient(context);
        return client.deleteSnippet(snippetId);
    }

    getSnippetsGroupCounts(
        context: ClientContext,
        params?: QueryParams
    ): AxiosPromise<{ counts: Record<number, number> }> {
        const client = this.getClient(context);
        return client.getSnippetsGroupCounts(params);
    }
}

export default new SnippetsClient();
